import React from 'react';
import { useDispatch } from 'react-redux';
import {
    Checkbox,
    DefaultButton,
    Dialog,
    DialogFooter,
    Icon,
    Link,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    useTheme,
} from '@fluentui/react';
import { debounce } from 'lodash';
import api, { GETTING_STARTED_TYPE, LIST, useCreateCrmTestOpportunityMutation, useEnsurePsaFieldInferenceMutation, useEnsurePsaProposalMutation, useGetCrmFolioCandidatesQuery, useGetExtensionFolioQuery, useGetPsaProposalQuery, usePatchExtensionFolioCommonFieldsMutation, usePutExtensionFolioMutation, useQuickSetupExtensionFolioMutation, useRecordPsaProposalDecisionMutation, useSetCrmFolioCapabilitiesMutation, useValidateExtensionFolioMutation } from '../../store/Api';
import { useSetCrmFolioStorefrontSlotsMutation } from '../../store/endpoints/StorefrontSettingsEndpoints';
import { toast } from 'react-toastify';
import { useApplyFolioQuoteLinkMutation, useGetFolioQuoteLinkPermissionQuery } from '../../store/endpoints/RecordLinksEndpoints';
import { useSetGettingStartedStepStatusMutation } from '../../store/endpoints/GettingStartedEndpoints';
import { ICrmFolioCandidate, ICrmFolioStorefrontSlotsRequest, ICrmTestOpportunityResponse, IHelpLink, IValidateExtensionResponse, PSA_PROPOSAL_DECISION, PSA_PROPOSAL_STATUS } from '../../models/Extension';
import { ExtensionStaticDataForm } from '../ExtensionInstallation/ExtensionDataForm';
import ExtensionValidationErrorModal from '../ExtensionInstallation/ExtensionValidationErrorModal';
import CrmCapabilitiesStep, { CAPABILITY_GROUPS, defaultSlotIdentifier } from './CrmCapabilitiesStep';
import CrmMappingStep, { hasMappableFields } from './CrmMappingStep';
import CrmProposalReviewStep, { IProposalApplication } from './CrmProposalReviewStep';
import { CRM_SETUP_STRINGS } from './wizardStrings';
import useSubOrganizationScope from '../../hooks/useSubOrganizationScope';

// The guided "Integrate your CRM" flow.
//
// The power-user route to the same result is the Extension Folio panel plus the storefront
// integrations pages, which ask the user to install the folio, tick the right sub-extensions,
// fill in credentials, and then find two other pages to wire what the connection unlocks. This
// dialog collapses all of that into one linear path: pick a CRM, enter credentials, choose what
// the connection should do (with the storefront wiring folded in), answer the required settings,
// and read a summary of what got turned on.
//
// It is deliberately not a second implementation of the credential form. The shared field renderer
// (ExtensionStaticDataForm) is reused as-is, so field types, options lookups and protected/password
// handling stay in one place; only the surrounding chrome is different.

/**
 * Which half of the setup this dialog is running.
 *
 * They are separate onboarding steps because the suggested settings cannot be worked out until
 * credentials exist: splitting them turns the wait into the gap between two checklist rows rather
 * than a spinner somebody watches. "connect" ends as soon as the connection authenticates;
 * "configure" picks up from there, by which time the suggestions are usually waiting.
 */
export type CrmSetupMode = 'connect' | 'configure';

interface ICrmSetupDialogProps {
    isOpen: boolean;
    onDismiss: () => void;
    /** Skip the picker and open straight onto this CRM (edit/re-run mode). The pick step's quick
     * setup still runs; it is idempotent and never removes what an earlier visit enabled. */
    initialCandidateId?: string;
    /** Defaults to the connecting half. */
    mode?: CrmSetupMode;
    /** Opens the connected folio's own settings panel, where imports can be run by hand. Supplied by
     * the page that owns that panel; without it the summary simply omits the link. */
    onOpenFolioSettings?: (installedId: string) => void;
}

type Step = 'pick' | 'proposal' | 'credentials' | 'connected' | 'capabilities' | 'mapping' | 'summary';

/** One selectable CRM. Kept local: nothing else picks from a folio candidate list. */
const CrmChoiceTile: React.FC<{
    candidate: ICrmFolioCandidate,
    disabled: boolean,
    onSelect: () => void
}> = ({ candidate, disabled, onSelect }) => {
    const theme = useTheme();
    const connected = candidate.lastValidationSuccess === true;

    return (
        <div
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-label={candidate.name}
            onClick={() => { if (!disabled) onSelect(); }}
            onKeyDown={(e) => {
                if (disabled) return;
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect();
                }
            }}
            style={{
                border: `1px solid ${theme.palette.neutralLight}`,
                borderRadius: 2,
                padding: '1em',
                cursor: disabled ? 'default' : 'pointer',
                opacity: disabled ? 0.6 : 1,
                background: theme.palette.white,
            }}
        >
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center" tokens={{ childrenGap: '0.5em' }}>
                <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>{candidate.name}</Text>
                {connected && (
                    <Stack
                        horizontal
                        verticalAlign="center"
                        tokens={{ childrenGap: '4px' }}
                        title={CRM_SETUP_STRINGS.connectedTooltip}
                    >
                        <Icon iconName="CheckMark" styles={{ root: { color: theme.palette.themePrimary } }} />
                        <Text variant="small" styles={{ root: { color: theme.palette.themePrimary } }}>
                            {CRM_SETUP_STRINGS.alreadyConnected}
                        </Text>
                    </Stack>
                )}
            </Stack>
            {candidate.description && (
                <Text variant="small" block styles={{ root: { color: theme.palette.neutralSecondary, marginTop: '0.5em' } }}>
                    {candidate.description}
                </Text>
            )}
        </div>
    );
};

/** "How to generate these credentials" pointers, if the folio publishes any. */
const HelpLinks: React.FC<{ links?: IHelpLink[] }> = ({ links }) => {
    const usable = (links ?? []).filter(l => !!l.url);
    if (usable.length === 0) return null;
    return (
        <Stack tokens={{ childrenGap: '0.5em' }}>
            <Text variant="small" styles={{ root: { fontWeight: 600 } }}>{CRM_SETUP_STRINGS.helpHeading}</Text>
            {usable.map((link, index) => (
                <Link key={`${link.url}-${index}`} href={link.url} target="_blank" rel="noopener noreferrer">
                    {link.text || link.url}
                </Link>
            ))}
        </Stack>
    );
};

const CrmSetupDialog: React.FC<ICrmSetupDialogProps> = (props) => {
    // CRM connections are folios, and folios are organization wide. Under a customer scope the
    // install would be stamped with the sub-organization, so the flow is closed off instead.
    const subOrganizationScope = useSubOrganizationScope();
    const theme = useTheme();
    const dispatch = useDispatch();

    const candidates = useGetCrmFolioCandidatesQuery(undefined, { skip: !props.isOpen });
    const [quickSetup] = useQuickSetupExtensionFolioMutation();
    const [saveCommonFields] = usePatchExtensionFolioCommonFieldsMutation();
    const [validateFolio] = useValidateExtensionFolioMutation();
    const [setCapabilities] = useSetCrmFolioCapabilitiesMutation();
    const [setStorefrontSlots] = useSetCrmFolioStorefrontSlotsMutation();
    const [putFolio] = usePutExtensionFolioMutation();
    const [ensureProposal] = useEnsurePsaProposalMutation();
    const [ensureFieldInference] = useEnsurePsaFieldInferenceMutation();
    const [recordProposalDecision] = useRecordPsaProposalDecisionMutation();
    const [setStepStatus] = useSetGettingStartedStepStatusMutation();
    const [createTestOpportunityMutation] = useCreateCrmTestOpportunityMutation();
    const [testingOpportunity, setTestingOpportunity] = React.useState(false);
    const [testResult, setTestResult] = React.useState<ICrmTestOpportunityResponse | undefined>();

    /** Creates one real opportunity from stand-in order details. Failures are shown rather than thrown:
     * a rejected test is the useful answer, not an error. */
    const createTestOpportunity = async () => {
        const sourceId = folioData?.sourceExtensionFolio?.id;
        if (!sourceId) return;
        setTestingOpportunity(true);
        setTestResult(undefined);
        try {
            setTestResult(await createTestOpportunityMutation(sourceId).unwrap());
        } catch {
            setTestResult({ success: false, error: CRM_SETUP_STRINGS.testOpportunityFailed });
        }
        setTestingOpportunity(false);
    };

    const mode: CrmSetupMode = props.mode ?? 'connect';
    const [step, setStep] = React.useState<Step>('pick');
    const [selected, setSelected] = React.useState<ICrmFolioCandidate | undefined>();
    const [preparing, setPreparing] = React.useState(false);
    const [validating, setValidating] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState<string | undefined>();
    const [validation, setValidation] = React.useState<IValidateExtensionResponse | undefined>();
    const [showValidationDetails, setShowValidationDetails] = React.useState(false);
    const [fields, setFields] = React.useState<{ [key: string]: string }>({});
    const [capabilitySelection, setCapabilitySelection] = React.useState<string[]>([]);
    const [slotChoices, setSlotChoices] = React.useState<ICrmFolioStorefrontSlotsRequest>({});
    const [subFields, setSubFields] = React.useState<{ [identifier: string]: { [field: string]: string } }>({});
    // Set when the API confirmed a proposal exists (or is being generated) for the picked CRM; the
    // API 404s for organizations the feature does not apply to, which simply skips the review step.
    const [proposalFolioId, setProposalFolioId] = React.useState<string | undefined>();
    const [proposalApplication, setProposalApplication] = React.useState<IProposalApplication | undefined>();
    // The inferred values this visit actually filled into empty fields, keyed "{sub}.{field}".
    // The keys double as the instrumentation keys; the values are what "accepted" is measured against.
    const inferredPrefills = React.useRef<{ [key: string]: string }>({});
    const [prefilledCount, setPrefilledCount] = React.useState(0);

    // The save and validate paths run async off a user gesture, so they read the latest values from
    // a ref rather than a closed-over render's copy.
    const fieldsRef = React.useRef<{ [key: string]: string }>({});
    const setFieldValues = React.useCallback((next: { [key: string]: string }) => {
        fieldsRef.current = next;
        setFields(next);
    }, []);
    const subFieldsRef = React.useRef<{ [identifier: string]: { [field: string]: string } }>({});
    const setSubFieldValues = React.useCallback((identifier: string, data: { [field: string]: string }) => {
        subFieldsRef.current = { ...subFieldsRef.current, [identifier]: data };
        setSubFields(subFieldsRef.current);
    }, []);

    const folio = useGetExtensionFolioQuery(selected?.id ?? '', { skip: !selected?.id || step === 'pick' });
    const folioData = folio.data;

    // The folio's quote-link quick-flow declaration, and whether the connected member may install it.
    // Offered on the capabilities step only when the target capability is being turned on and the
    // provider says the member can write its quote-link setup table.
    const folioQuoteLink = folioData?.sourceExtensionFolio?.quoteLink;
    const quoteLinkPermission = useGetFolioQuoteLinkPermissionQuery(folioData?.sourceExtensionFolio?.id ?? '', {
        skip: step !== 'capabilities' || !folioQuoteLink?.permissionUrl || !folioData?.sourceExtensionFolio?.id,
    });
    const [applyQuoteLink] = useApplyFolioQuoteLinkMutation();
    const [createQuoteLinkChecked, setCreateQuoteLinkChecked] = React.useState(true);
    const quoteLinkOffered = !!folioQuoteLink?.subExtension
        && quoteLinkPermission.data?.allowed === true
        && capabilitySelection.includes(folioQuoteLink.subExtension);
    const sharedFields = folioData?.sourceExtensionFolio?.sharedFields ?? [];

    // The proposal, polled while the worker is generating it (the restyle assistant's own idiom:
    // interval 0 rather than skip once it settles, so the cached data is not dropped).
    // Polling looks at the previous render's data (the standard trick for options that depend on the
    // query's own result); one render of lag on stopping is harmless.
    const lastProposalFieldsPending = React.useRef(false);
    const proposalQuery = useGetPsaProposalQuery(proposalFolioId ?? '', {
        skip: !proposalFolioId,
        pollingInterval: proposalFolioId &&
            (step === 'proposal' ||
             ((step === 'capabilities' || step === 'mapping') && lastProposalFieldsPending.current))
            ? 3000 : 0,
    });
    const proposal = proposalQuery.data;
    lastProposalFieldsPending.current = proposal?.fieldInferenceStatus === PSA_PROPOSAL_STATUS.pending;

    // A proposal that cannot arrive (worker skipped or failed, or the read errors) must not hold the
    // wizard hostage; the plain flow is always the fallback.
    React.useEffect(() => {
        if (step !== 'proposal') return;
        if (proposalQuery.isError ||
            proposal?.status === PSA_PROPOSAL_STATUS.skipped ||
            proposal?.status === PSA_PROPOSAL_STATUS.failed) {
            setStep('credentials');
        }
    }, [step, proposalQuery.isError, proposal?.status]);

    // Record that a person saw the proposal, once. This is the denominator of the acceptance rate.
    const shownRecorded = React.useRef(false);
    React.useEffect(() => {
        if (step !== 'proposal' || proposal?.status !== PSA_PROPOSAL_STATUS.ready) return;
        if (shownRecorded.current || !proposalFolioId) return;
        shownRecorded.current = true;
        void recordProposalDecision({
            folioId: proposalFolioId,
            request: { decision: PSA_PROPOSAL_DECISION.shown },
        });
    }, [step, proposal?.status, proposalFolioId, recordProposalDecision]);

    // Whether the connector can check credentials at all. The detail view knows; the candidate is
    // the fallback while it loads.
    const canValidate = folioData?.hasValidation ?? selected?.hasValidation ?? true;

    // Seed the form from the server once per folio. A later refetch (or the optimistic update the
    // save mutation applies) must not stomp on what the user is typing.
    const seededFor = React.useRef<string | undefined>(undefined);
    React.useEffect(() => {
        const folioId = folioData?.id;
        if (!folioId || seededFor.current === folioId) return;
        seededFor.current = folioId;
        setFieldValues({ ...(folioData?.commonInstalledFields ?? {}) });
        subFieldsRef.current = { ...(folioData?.subExtensionInstalledFields ?? {}) };
        setSubFields(subFieldsRef.current);
    }, [folioData?.id, folioData?.commonInstalledFields, folioData?.subExtensionInstalledFields, setFieldValues]);

    const reset = React.useCallback(() => {
        setStep('pick');
        setSelected(undefined);
        setPreparing(false);
        setValidating(false);
        setSaving(false);
        setErrorMessage(undefined);
        setValidation(undefined);
        setShowValidationDetails(false);
        setFieldValues({});
        setCapabilitySelection([]);
        setSlotChoices({});
        subFieldsRef.current = {};
        setSubFields({});
        setProposalFolioId(undefined);
        setProposalApplication(undefined);
        shownRecorded.current = false;
        inferredPrefills.current = {};
        setPrefilledCount(0);
        configureSeeded.current = false;
        seededFor.current = undefined;
    }, [setFieldValues]);

    // Everything is per-visit state; a reopen should start on the picker rather than wherever the
    // user happened to leave off.
    React.useEffect(() => {
        if (!props.isOpen) reset();
    }, [props.isOpen, reset]);

    // Edit/re-run: a caller that names a CRM skips the picker. Auto-pick once per open, and only
    // from the pick step, so backing out to the picker by hand is still possible.
    const autoPicked = React.useRef(false);
    React.useEffect(() => {
        if (!props.isOpen) { autoPicked.current = false; return; }
        if (autoPicked.current || !props.initialCandidateId || step !== 'pick' || selected) return;
        const candidate = candidates.data?.items?.find(c => c.id === props.initialCandidateId);
        if (!candidate) return;
        autoPicked.current = true;
        void pick(candidate);
    }, [props.isOpen, props.initialCandidateId, step, selected, candidates.data]);

    /**
     * Persist the credential fields onto the installed folio. This goes through the commonFields PATCH, not
     * the whole-folio PUT: the PUT replaces the stored document with whatever the client holds, so a stale
     * copy could silently disable sub-extensions an admin enabled elsewhere. The PATCH merges credentials
     * only, making that impossible from this dialog.
     */
    const persist = React.useCallback(async (sourceId: string | undefined, values: { [key: string]: string }) => {
        if (!sourceId) return;
        await saveCommonFields({ extensionId: sourceId, fields: values });
    }, [saveCommonFields]);

    const debouncedPersist = React.useMemo(
        () => debounce((sourceId: string | undefined, values: { [key: string]: string }) => {
            void persist(sourceId, values);
        }, 1000, { leading: false }),
        [persist]);

    React.useEffect(() => () => debouncedPersist.cancel(), [debouncedPersist]);

    const pick = async (candidate: ICrmFolioCandidate) => {
        setErrorMessage(undefined);
        setSelected(candidate);
        setPreparing(true);
        try {
            const result = await quickSetup(candidate.id).unwrap();
            if (result && result.success === false) {
                setErrorMessage(result.message || CRM_SETUP_STRINGS.setupFailed);
                setSelected(undefined);
                setPreparing(false);
                return;
            }
            // Wedge-sourced organizations get a proposed setup to review first. The API answers 404
            // for everyone else (and 503 when the feature is off), which is the ordinary no-proposal
            // path, not an error worth surfacing.
            try {
                const ensured = await ensureProposal(candidate.id).unwrap();
                if (ensured?.status && ensured.status !== PSA_PROPOSAL_STATUS.skipped &&
                    ensured.status !== PSA_PROPOSAL_STATUS.failed) {
                    setProposalFolioId(candidate.id);
                    setStep('proposal');
                } else {
                    setStep('credentials');
                }
            } catch {
                setStep('credentials');
            }
        } catch {
            setErrorMessage(CRM_SETUP_STRINGS.setupFailed);
            setSelected(undefined);
        }
        setPreparing(false);
    };

    const missingRequired = sharedFields.some(f => !f.optional && (fields[f.field] ?? '') === '');

    /**
     * Move from credentials to the capabilities step, seeding its state from what the server holds
     * right now: the installed record's enabled set (an edit visit reflects earlier choices), and
     * the slots that already point at this connection (resending them is idempotent, and it makes
     * the selection visible instead of a silent "leave alone").
     */
    const seedCapabilities = React.useCallback((candidate: ICrmFolioCandidate | undefined) => {
        const serverEnabled = folio.data?.enabledSubExtensions ?? candidate?.defaultSubExtensions ?? [];
        // Accepted proposal capabilities join what the server already has; the proposal prefills, it
        // never turns off something an earlier visit enabled.
        const enabled = proposalApplication
            ? [...serverEnabled, ...proposalApplication.subExtensions.filter(s => !serverEnabled.includes(s))]
            : serverEnabled;
        setCapabilitySelection(enabled);

        const slots: ICrmFolioStorefrontSlotsRequest = {};
        (['opportunity', 'customerLookup', 'locationLookup'] as (keyof ICrmFolioStorefrontSlotsRequest)[])
            .forEach(slot => {
                const stored = candidate?.storefrontSlots?.[slot];
                if (stored?.subExtension) {
                    slots[slot] = stored.subExtension;
                } else if (candidate && !stored?.disabled && !stored?.assignedElsewhere) {
                    // An unset slot follows its capability when that capability is on.
                    const group = CAPABILITY_GROUPS.find(g => g.slot === slot);
                    const members = candidate.capabilities?.filter(c =>
                        !!c.extensionType && group?.types.includes(c.extensionType)) ?? [];
                    if (group && members.some(m => enabled.includes(m.identifier))) {
                        slots[slot] = defaultSlotIdentifier(candidate, slot);
                    }
                }
            });
        // Accepted proposal slots win over the derived defaults: the person just confirmed them.
        if (proposalApplication) {
            if (proposalApplication.slots.opportunity) slots.opportunity = proposalApplication.slots.opportunity;
            if (proposalApplication.slots.customerLookup) slots.customerLookup = proposalApplication.slots.customerLookup;
            if (proposalApplication.slots.locationLookup) slots.locationLookup = proposalApplication.slots.locationLookup;
        }
        setSlotChoices(slots);
    }, [folio.data?.enabledSubExtensions, proposalApplication]);

    const enterCapabilities = React.useCallback((candidate: ICrmFolioCandidate | undefined) => {
        seedCapabilities(candidate);
        setStep('capabilities');
    }, [seedCapabilities]);

    /**
     * Configure mode opens past the connecting half, on whichever CRM is already connected. The
     * checklist only offers this step once one has authenticated, so a missing candidate means the
     * checklist and the folio disagree; falling back to the picker is better than an empty dialog.
     */
    React.useEffect(() => {
        if (!props.isOpen || mode !== 'configure' || step !== 'pick' || selected) return;
        const items = candidates.data?.items ?? [];
        if (items.length === 0) return;
        const connected = props.initialCandidateId
            ? items.find(c => c.id === props.initialCandidateId)
            : items.find(c => c.lastValidationSuccess === true) ?? items.find(c => c.installed);
        if (!connected) return;
        setSelected(connected);
        // Point the proposal query at this folio. Configure mode reaches neither of the places that
        // normally do it (the picker, and validating credentials), so without this the suggestions
        // prepared while the operator was away are fetched by nobody and the step they exist for
        // renders empty.
        setProposalFolioId(connected.id);
        // Idempotent: a ready pass is returned as it stands. This covers the operator who entered
        // credentials outside the wizard, where nothing has ever queued the pass.
        void ensureFieldInference(connected.id).unwrap().catch(() => { /* not available here */ });
        setStep('capabilities');
    }, [props.isOpen, props.initialCandidateId, mode, step, selected, candidates.data, ensureFieldInference]);

    // Seed the capabilities step once its folio has loaded. In connect mode the credentials step has
    // already done this; in configure mode this is the first chance, since the dialog opened straight
    // onto it and the folio detail arrives a moment later.
    const configureSeeded = React.useRef(false);
    React.useEffect(() => {
        if (mode !== 'configure' || step !== 'capabilities' || configureSeeded.current) return;
        if (!folio.data || !freshCandidate) return;
        configureSeeded.current = true;
        seedCapabilities(freshCandidate);
        // eslint-disable-next-line
    }, [mode, step, folio.data, seedCapabilities]);

    /**
     * The person confirmed (some of) the proposal. Record the verdicts, then fold the accepted
     * values into the wizard's own state: shared values and per-sub-extension values fill only
     * fields that are still empty, because a value somebody stored earlier outranks a guess.
     */
    const applyProposal = (application: IProposalApplication) => {
        setProposalApplication(application);
        if (proposalFolioId) {
            void recordProposalDecision({
                folioId: proposalFolioId,
                request: {
                    decision: application.allAccepted
                        ? PSA_PROPOSAL_DECISION.accepted
                        : PSA_PROPOSAL_DECISION.edited,
                    fieldDecisions: application.fieldDecisions,
                },
            });
        }

        const mergedShared = { ...fieldsRef.current };
        Object.entries(application.sharedValues).forEach(([field, value]) => {
            if ((mergedShared[field] ?? '') === '') mergedShared[field] = value;
        });
        setFieldValues(mergedShared);

        Object.entries(application.subFieldValues).forEach(([identifier, values]) => {
            const current = { ...(subFieldsRef.current[identifier] ?? {}) };
            Object.entries(values).forEach(([field, value]) => {
                if ((current[field] ?? '') === '') current[field] = value;
            });
            setSubFieldValues(identifier, current);
        });

        setStep('credentials');
    };

    /**
     * Kicks off the post-credential suggested-settings pass. Fire-and-forget: a 404 means the feature
     * is off or does not apply here, which is the ordinary no-suggestions path, not an error. On
     * success the proposal query starts (or keeps) polling so the mapping step sees the values land.
     */
    const requestFieldInference = (sourceId: string) => {
        void ensureFieldInference(sourceId).unwrap().then(() => {
            setProposalFolioId(current => current ?? sourceId);
        }).catch(() => { /* not available here; the wizard works the same without suggestions */ });
    };

    const skipProposal = () => {
        if (proposalFolioId && proposal?.status === PSA_PROPOSAL_STATUS.ready) {
            void recordProposalDecision({
                folioId: proposalFolioId,
                request: { decision: PSA_PROPOSAL_DECISION.dismissed },
            });
        }
        setProposalApplication(undefined);
        setStep('credentials');
    };

    const validate = async () => {
        const sourceId = folioData?.sourceExtensionFolio?.id;
        if (!folioData?.id || !sourceId) return;

        setValidating(true);
        setErrorMessage(undefined);
        setValidation(undefined);

        const values = fieldsRef.current;
        debouncedPersist.cancel();
        try {
            // Save first: validation checks the credentials we were handed, but the folio has to end
            // up holding them or the next scheduled run would go out with the old ones.
            await persist(sourceId, values);
            if (!canValidate) {
                // Nothing to check against; saved credentials are as far as this connector goes.
                requestFieldInference(sourceId);
                if (mode === 'connect') setStep('connected');
                else enterCapabilities(selected);
                setValidating(false);
                return;
            }
            const result = await validateFolio({
                id: sourceId,
                request: {
                    additionalData: values,
                    folioId: folioData.id,
                    validationActions: folioData.enabledSubExtensions,
                }
            }).unwrap();
            setValidation(result);
            if (result?.success) {
                // The server decides whether the checklist step is done; just make the ring re-read it.
                dispatch(api.util.invalidateTags([{ type: GETTING_STARTED_TYPE, id: LIST }]));
                // Credentials work, so the connector's option lists are now fetchable: kick off the
                // suggested-settings pass. In the split flow this is the point of the split, since the
                // work now runs while the operator is back on the checklist rather than waiting here.
                requestFieldInference(sourceId);
                if (mode === 'connect') setStep('connected');
                else enterCapabilities(selected);
            } else {
                setErrorMessage(result?.error || result?.message || CRM_SETUP_STRINGS.validateFailed);
            }
        } catch {
            setErrorMessage(CRM_SETUP_STRINGS.validateFailed);
        }
        setValidating(false);
    };

    /** Persist the capabilities step: the explicit enabled set, then the storefront slot wiring. */
    const saveCapabilities = async () => {
        const sourceId = folioData?.sourceExtensionFolio?.id;
        if (!sourceId) return;
        setSaving(true);
        setErrorMessage(undefined);
        try {
            const result = await setCapabilities({
                folioId: sourceId,
                request: { enabledSubExtensions: capabilitySelection },
            }).unwrap();
            if (result && result.success === false) {
                setErrorMessage(result.message || CRM_SETUP_STRINGS.capabilitiesSaveFailed);
                setSaving(false);
                return;
            }
            const requested = Object.fromEntries(
                Object.entries(slotChoices).filter(([, value]) => value !== undefined));
            if (Object.keys(requested).length > 0) {
                await setStorefrontSlots({ folioId: sourceId, request: requested }).unwrap();
            }
            // Best-effort: the capabilities are saved either way, and the quote link can always be
            // added later from the folio settings.
            if (quoteLinkOffered && createQuoteLinkChecked && folioQuoteLink?.subExtension) {
                const link = `${window.location.origin}/go/opportunity-import/`
                    + encodeURIComponent(`folio_${sourceId}_${folioQuoteLink.subExtension}`)
                    + (folioQuoteLink.parameter ? `?${folioQuoteLink.parameter}` : '');
                try {
                    const quoteLinkResult = await applyQuoteLink({ folioId: sourceId, link }).unwrap();
                    if (quoteLinkResult.success === false) toast.error(CRM_SETUP_STRINGS.quoteLinkFailed);
                    else toast.success(quoteLinkResult.alreadyExisted
                        ? CRM_SETUP_STRINGS.quoteLinkExisted
                        : CRM_SETUP_STRINGS.quoteLinkCreated);
                } catch {
                    toast.error(CRM_SETUP_STRINGS.quoteLinkFailed);
                }
            }
            if (hasMappableFields(folioData, capabilitySelection)) {
                setStep('mapping');
            } else {
                setStep('summary');
            }
        } catch {
            setErrorMessage(CRM_SETUP_STRINGS.capabilitiesSaveFailed);
        }
        setSaving(false);
    };

    /**
     * Persist the mapping step. This is the one whole-document PUT in the dialog: there is no
     * narrower route for sub-extension fields. The document was fetched moments ago and the enabled
     * set is overwritten with this visit's own selection, so the stale-copy hazard the credentials
     * comment describes does not apply here.
     */
    const saveMapping = async () => {
        const sourceId = folioData?.sourceExtensionFolio?.id;
        if (!folioData || !sourceId) return;
        setSaving(true);
        setErrorMessage(undefined);
        try {
            const result = await putFolio({
                extensionId: sourceId,
                request: {
                    ...folioData,
                    enabledSubExtensions: capabilitySelection,
                    subExtensionInstalledFields: {
                        ...(folioData.subExtensionInstalledFields ?? {}),
                        ...subFieldsRef.current,
                    },
                },
            }).unwrap();
            if (result && result.success === false) {
                setErrorMessage(result.message || CRM_SETUP_STRINGS.mappingSaveFailed);
                setSaving(false);
                return;
            }
            recordFieldSuggestionVerdicts(sourceId);
            setStep('summary');
        } catch {
            setErrorMessage(CRM_SETUP_STRINGS.mappingSaveFailed);
        }
        setSaving(false);
    };

    /**
     * The acceptance record for the suggested settings: for every field this visit prefilled, whether
     * the saved value is the suggestion (accepted), something else (edited), or cleared (rejected).
     * Fire-and-forget; measuring must never make saving fail.
     */
    const recordFieldSuggestionVerdicts = (sourceId: string) => {
        const keys = Object.keys(inferredPrefills.current);
        if (keys.length === 0) return;
        const fieldDecisions: { [key: string]: string } = {};
        keys.forEach(key => {
            const separator = key.indexOf('.');
            const identifier = key.slice(0, separator);
            const field = key.slice(separator + 1);
            const finalValue = subFieldsRef.current[identifier]?.[field] ?? '';
            fieldDecisions[key] = finalValue === inferredPrefills.current[key]
                ? 'accepted'
                : finalValue === '' ? 'rejected' : 'edited';
        });
        void recordProposalDecision({
            folioId: proposalFolioId ?? sourceId,
            request: { fieldDecisions },
        });
    };

    const busy = preparing || validating || saving;
    const items = candidates.data?.items ?? [];
    const helpLinks = selected?.helpLinks ?? folioData?.sourceExtensionFolio?.helpLinks;
    // The freshest view of the picked CRM: the candidates list refetches after every wizard write.
    const freshCandidate = items.find(c => c.id === selected?.id) ?? selected;

    const renderPick = () => {
        if (subOrganizationScope.isScoped) {
            return (
                <MessageBar messageBarType={MessageBarType.info}>
                    A CRM connection is installed for the whole organization, not for one customer. Switch the
                    customer scope back to All customers to connect a CRM.
                </MessageBar>
            );
        }
        if (candidates.isLoading || candidates.isFetching) {
            return <Spinner size={SpinnerSize.large} label={CRM_SETUP_STRINGS.loadingCandidates} />;
        }
        if (candidates.isError) {
            return <MessageBar messageBarType={MessageBarType.error}>{CRM_SETUP_STRINGS.loadFailed}</MessageBar>;
        }
        if (items.length === 0) {
            return <MessageBar messageBarType={MessageBarType.info}>{CRM_SETUP_STRINGS.empty}</MessageBar>;
        }
        return (
            <Stack tokens={{ childrenGap: '0.5em' }}>
                {items.map(candidate => (
                    <CrmChoiceTile
                        key={candidate.id}
                        candidate={candidate}
                        disabled={busy}
                        onSelect={() => void pick(candidate)}
                    />
                ))}
                {preparing && <Spinner size={SpinnerSize.small} label={CRM_SETUP_STRINGS.preparing} labelPosition="right" />}
            </Stack>
        );
    };

    const renderProposal = () => (
        <CrmProposalReviewStep
            proposal={proposal}
            candidate={freshCandidate}
            pending={!proposal || proposal.status === PSA_PROPOSAL_STATUS.pending}
            onApply={applyProposal}
            onSkip={skipProposal}
        />
    );

    const renderCredentials = () => {
        if (folio.isLoading || folio.isFetching || !folioData) {
            return <Spinner size={SpinnerSize.large} label={CRM_SETUP_STRINGS.loadingFields} />;
        }
        return (
            <Stack tokens={{ childrenGap: '1em' }}>
                <Text variant="small">{CRM_SETUP_STRINGS.credentialsIntro(selected?.name ?? folioData.name ?? 'your CRM')}</Text>
                {!canValidate && (
                    <MessageBar messageBarType={MessageBarType.info}>{CRM_SETUP_STRINGS.cannotValidate}</MessageBar>
                )}
                <ExtensionStaticDataForm
                    staticData={fields}
                    additionalFields={sharedFields}
                    extensionId={`folio_${folioData.sourceExtensionFolio?.id ?? ''}_shared`}
                    updateOnDisengage
                    onChange={(data) => {
                        setFieldValues(data);
                        debouncedPersist(folioData.sourceExtensionFolio?.id, data);
                    }}
                />
                <HelpLinks links={helpLinks} />
            </Stack>
        );
    };

    // Reaching the summary means every choice has been saved, so the configuring step is done. The
    // server cannot work this out for itself: a folio with values in it looks the same whether somebody
    // reviewed them or an import wrote them, and "I am happy with this" is exactly what the step means.
    const markedConfigured = React.useRef(false);
    React.useEffect(() => {
        if (mode !== 'configure' || step !== 'summary' || markedConfigured.current) return;
        markedConfigured.current = true;
        void setStepStatus({ id: 'configure-integrations', status: 'complete' });
    }, [mode, step, setStepStatus]);

    const renderConnected = () => (
        <Stack tokens={{ childrenGap: '1em' }}>
            <MessageBar messageBarType={MessageBarType.success}>{CRM_SETUP_STRINGS.connectedHeading}</MessageBar>
            <Text variant="small">{CRM_SETUP_STRINGS.connectedBody}</Text>
        </Stack>
    );

    const renderCapabilities = () => {
        if (!freshCandidate) return null;
        return (
            <Stack tokens={{ childrenGap: '1em' }}>
                <CrmCapabilitiesStep
                    candidate={freshCandidate}
                    selection={capabilitySelection}
                    onSelectionChange={setCapabilitySelection}
                    slots={slotChoices}
                    onSlotsChange={setSlotChoices}
                />
                {quoteLinkOffered && (
                    <Stack tokens={{ childrenGap: '0.5em' }}>
                        <Checkbox
                            label={CRM_SETUP_STRINGS.quoteLinkOffer}
                            checked={createQuoteLinkChecked}
                            onChange={(_, checked) => setCreateQuoteLinkChecked(checked === true)}
                        />
                        <Text variant="small" styles={{ root: { marginLeft: '2em' } }}>
                            {CRM_SETUP_STRINGS.quoteLinkOfferDetail}
                        </Text>
                    </Stack>
                )}
                {saving && <Spinner size={SpinnerSize.small} label={CRM_SETUP_STRINGS.savingCapabilities} labelPosition="right" />}
            </Stack>
        );
    };

    // Fold ready suggestions into the mapping form: only into fields that are still empty (a stored
    // value outranks a guess), each applied once so a poll refresh cannot stomp on later edits.
    React.useEffect(() => {
        if (step !== 'mapping') return;
        if (proposal?.fieldInferenceStatus !== PSA_PROPOSAL_STATUS.ready) return;
        const inferred = proposal.inferredSubExtensionFieldValues;
        if (!inferred) return;
        Object.entries(inferred).forEach(([identifier, values]) => {
            if (!capabilitySelection.includes(identifier)) return;
            const current = { ...(subFieldsRef.current[identifier] ?? {}) };
            let changed = false;
            Object.entries(values).forEach(([field, value]) => {
                const key = `${identifier}.${field}`;
                if (inferredPrefills.current[key] !== undefined) return;
                if ((current[field] ?? '') !== '') return;
                current[field] = value;
                inferredPrefills.current[key] = value;
                changed = true;
            });
            if (changed) setSubFieldValues(identifier, current);
        });
        setPrefilledCount(Object.keys(inferredPrefills.current).length);
    }, [step, proposal?.fieldInferenceStatus, proposal?.inferredSubExtensionFieldValues,
        capabilitySelection, setSubFieldValues]);

    const renderMapping = () => {
        if (!folioData) return null;
        return (
            <Stack tokens={{ childrenGap: '1em' }}>
                {proposal?.fieldInferenceStatus === PSA_PROPOSAL_STATUS.pending && (
                    <MessageBar messageBarType={MessageBarType.info}>
                        {CRM_SETUP_STRINGS.mappingSuggestionsChecking}
                    </MessageBar>
                )}
                {prefilledCount > 0 && (
                    <MessageBar messageBarType={MessageBarType.info}>
                        {CRM_SETUP_STRINGS.mappingSuggestionsApplied(prefilledCount)}
                    </MessageBar>
                )}
                <CrmMappingStep
                    folioData={folioData}
                    enabledSubExtensions={capabilitySelection}
                    subFields={subFields}
                    onChange={setSubFieldValues}
                />
            </Stack>
        );
    };

    const renderSummary = () => {
        const candidate = freshCandidate;
        const name = candidate?.name ?? 'Your CRM';
        const enabledGroups = CAPABILITY_GROUPS.filter(group =>
            (candidate?.capabilities ?? []).some(c =>
                !!c.extensionType && group.types.includes(c.extensionType) &&
                capabilitySelection.includes(c.identifier)));
        const wiredSlots = ([
            ['opportunity', CRM_SETUP_STRINGS.slotOpportunity],
            ['customerLookup', CRM_SETUP_STRINGS.slotCustomerLookup],
            ['locationLookup', CRM_SETUP_STRINGS.slotLocationLookup],
        ] as [keyof ICrmFolioStorefrontSlotsRequest, string][])
            .filter(([slot]) => !!candidate?.storefrontSlots?.[slot]?.subExtension);

        return (
            <Stack tokens={{ childrenGap: '1em' }}>
                <MessageBar messageBarType={MessageBarType.success}>
                    {CRM_SETUP_STRINGS.success}
                    {props.onOpenFolioSettings && folioData?.id && (
                        <>
                            {' '}
                            <Link onClick={() => {
                                const installedId = folioData.id!;
                                props.onDismiss();
                                props.onOpenFolioSettings!(installedId);
                            }}>
                                {CRM_SETUP_STRINGS.successRunImport}
                            </Link>
                        </>
                    )}
                </MessageBar>
                <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
                    {CRM_SETUP_STRINGS.summaryHeading(name)}
                </Text>
                <Stack tokens={{ childrenGap: '0.25em' }}>
                    <Text variant="small">
                        {canValidate ? CRM_SETUP_STRINGS.summaryVerified : CRM_SETUP_STRINGS.summaryNotVerified}
                    </Text>
                    {enabledGroups.map(group => (
                        <Text key={group.key} variant="small">{CRM_SETUP_STRINGS.summaryCapabilityOn(group.label)}</Text>
                    ))}
                    {wiredSlots.map(([slot, label]) => (
                        <Text key={slot} variant="small">{CRM_SETUP_STRINGS.summarySlotWired(label)}</Text>
                    ))}
                </Stack>
                <Text variant="small" styles={{ root: { color: theme.palette.neutralSecondary } }}>
                    {CRM_SETUP_STRINGS.summaryChecklistNote}
                </Text>

                {/* Offered here because this is the moment the choices are fresh and still cheap to
                    change. It writes a real record, so the copy says so before the click, not after. */}
                <Stack tokens={{ childrenGap: '0.5em' }}>
                    <Text variant="small">{CRM_SETUP_STRINGS.testOpportunityIntro}</Text>
                    {testResult?.success !== true && (
                        <Stack horizontal>
                            <DefaultButton
                                text={testingOpportunity
                                    ? CRM_SETUP_STRINGS.testOpportunityRunning
                                    : CRM_SETUP_STRINGS.testOpportunityAction}
                                disabled={testingOpportunity || !folioData?.sourceExtensionFolio?.id}
                                onClick={() => void createTestOpportunity()}
                            />
                        </Stack>
                    )}
                    {testResult?.success === true && (
                        <MessageBar messageBarType={MessageBarType.success}>
                            {CRM_SETUP_STRINGS.testOpportunitySuccess(testResult.opportunityId)}
                            {testResult.url && (
                                <>
                                    {' '}
                                    <Link href={testResult.url} target="_blank" rel="noopener noreferrer">
                                        {CRM_SETUP_STRINGS.testOpportunityOpen}
                                    </Link>
                                </>
                            )}
                        </MessageBar>
                    )}
                    {testResult && testResult.success !== true && (
                        <MessageBar messageBarType={MessageBarType.error}>
                            {testResult.error || CRM_SETUP_STRINGS.testOpportunityFailed}
                        </MessageBar>
                    )}
                </Stack>
            </Stack>
        );
    };

    const renderFooter = () => {
        switch (step) {
            case 'credentials':
                return (
                    <DialogFooter>
                        <PrimaryButton
                            text={validating
                                ? CRM_SETUP_STRINGS.validatingLabel
                                : canValidate ? CRM_SETUP_STRINGS.validateLabel : CRM_SETUP_STRINGS.saveContinueLabel}
                            disabled={validating || missingRequired || folio.isFetching}
                            title={missingRequired ? CRM_SETUP_STRINGS.missingFields : undefined}
                            onClick={() => void validate()}
                        />
                        <DefaultButton
                            text={CRM_SETUP_STRINGS.back}
                            disabled={validating}
                            onClick={() => {
                                debouncedPersist.cancel();
                                setSelected(undefined);
                                setValidation(undefined);
                                setErrorMessage(undefined);
                                setFieldValues({});
                                seededFor.current = undefined;
                                setStep('pick');
                            }}
                        />
                    </DialogFooter>
                );
            case 'capabilities':
                return (
                    <DialogFooter>
                        <PrimaryButton
                            text={CRM_SETUP_STRINGS.capabilitiesNext}
                            disabled={saving}
                            onClick={() => void saveCapabilities()}
                        />
                        <DefaultButton
                            text={CRM_SETUP_STRINGS.close}
                            disabled={saving}
                            onClick={props.onDismiss}
                        />
                    </DialogFooter>
                );
            case 'mapping':
                return (
                    <DialogFooter>
                        <PrimaryButton
                            text={CRM_SETUP_STRINGS.mappingNext}
                            disabled={saving || folio.isFetching}
                            onClick={() => void saveMapping()}
                        />
                        <DefaultButton
                            text={CRM_SETUP_STRINGS.close}
                            disabled={saving}
                            onClick={props.onDismiss}
                        />
                    </DialogFooter>
                );
            case 'connected':
                return (
                    <DialogFooter>
                        <PrimaryButton text={CRM_SETUP_STRINGS.connectedDone} onClick={props.onDismiss} />
                    </DialogFooter>
                );
            case 'summary':
                return (
                    <DialogFooter>
                        <PrimaryButton text={CRM_SETUP_STRINGS.done} onClick={props.onDismiss} />
                    </DialogFooter>
                );
            default:
                return (
                    <DialogFooter>
                        <DefaultButton text={CRM_SETUP_STRINGS.cancel} disabled={busy} onClick={props.onDismiss} />
                    </DialogFooter>
                );
        }
    };

    if (!props.isOpen) return null;

    return (<>
        <ExtensionValidationErrorModal
            errorDetails={validation}
            displayModal={showValidationDetails}
            closer={() => setShowValidationDetails(false)}
        />
        <Dialog
            hidden={false}
            onDismiss={() => { if (!busy) props.onDismiss(); }}
            minWidth={560}
            dialogContentProps={{
                title: mode === 'configure' ? CRM_SETUP_STRINGS.configureTitle : CRM_SETUP_STRINGS.title,
                showCloseButton: true,
            }}
            modalProps={{ isBlocking: true }}
        >
            <Stack tokens={{ childrenGap: '1em' }}>
                {step === 'pick' && (
                    <Stack horizontal tokens={{ childrenGap: '0.5em' }} verticalAlign="start">
                        <Icon iconName="PlugConnected" styles={{ root: { fontSize: 20, color: theme.palette.themePrimary } }} />
                        <Text variant="small">{CRM_SETUP_STRINGS.intro}</Text>
                    </Stack>
                )}

                {errorMessage && (
                    <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setErrorMessage(undefined)}>
                        {errorMessage}
                        {(validation?.validationMessages?.length ?? 0) > 0 && (
                            <Link onClick={() => setShowValidationDetails(true)}>&nbsp;{CRM_SETUP_STRINGS.moreInfo}</Link>
                        )}
                    </MessageBar>
                )}

                {step === 'pick' && renderPick()}
                {step === 'proposal' && renderProposal()}
                {step === 'credentials' && renderCredentials()}
                {step === 'connected' && renderConnected()}
                {step === 'capabilities' && renderCapabilities()}
                {step === 'mapping' && renderMapping()}
                {step === 'summary' && renderSummary()}
            </Stack>
            {renderFooter()}
        </Dialog>
    </>);
};

export default CrmSetupDialog;
